import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type MetadataGlossaryApiId, type MetadataGlossaryV1 } from '@/model/Metadata.model';
import { type TeamGlossary, type TeamGlossaryApiId } from '@/model/Glosary.model';
import Loading from '@/components/Loading';
import { type Nullable, type NumericString, type RequiredNonNil } from '@/model/Common.model';
import { _isEmpty, _isNil, _isNotBlank, _isStringifiedNullish, _noop, _notNil } from '@/littledash';
import { generateTempApiId, isApiId } from '@/utils/ApiId.utils';
import { errorToast, generateIntegrity } from '@/helpers';
import {
  type GroupTarget,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
  type SampleDefinitionDetailV1,
  type SampleDefinitionFormData,
  type SampleDefinitionMetadataV1,
  type SampleDefinitionTargetFormData,
  type SampleDetailType,
  type SampleDetailUnit,
  type TempSampleTypeId,
} from '@/model/SamplePlan.model';
import { setsDifference } from '@/utils/browser.utils';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { useSelector } from 'react-redux';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { useModalAction } from '@/utils/modal';

type OnSubmitFunction = (
  updated: Array<SampleDefinitionFormData>,
  deleted: Set<SampleDefinitionFormData['api_id']>,
  reasonForChange?: string
) => Promise<void>;

export type SampleDefinitionRowData = Omit<
  SampleDefinitionFormData,
  'metadata' | 'details' | 'sample_type' | 'target'
> &
  Record<MetadataGlossaryApiId, string> &
  Partial<Record<SampleDetailType, string>>;

export type TempSampleData = { type: TempSampleTypeId };
export type SampleData = {
  type: TeamGlossaryApiId;
  definitions: Array<SampleDefinitionRowData>;
} & Partial<Record<SampleDetailType, SampleDetailUnit>>;
export type PlannedSampleData = TempSampleData | SampleData;
export type Step = 'sample_type_define' | 'assign_animals';

const SampleDefinitionDetailTypes: Record<SampleDetailType, string> = {
  weight: 'Weight',
  depth: 'Depth',
  volume: ' Volume',
  width: 'Width',
  length: 'Length',
} as const;

/**
 * Manages state for sample plan creation and editing with event-driven updates.
 *
 * Responsibilities:
 * - Maintains planned sample definitions, animal assignments, and form validation state
 * - Detects cascading updates that will affect existing planned samples
 * - Emits events when state changes to trigger React re-renders via custom hooks
 *
 * Cascade Detection:
 * - Compares current sample definitions against initial state using SHA-512 hashes
 * - Tracks changes to sample types, detail values/units, and metadata values
 * - Calculates total count of planned samples affected by changes
 * - Automatically disabled during study creation (no existing samples to affect)
 *
 * Event System:
 * - Uses EventTarget for standard browser event handling
 * - Custom events for each state change type (see EVENTS constants)
 * - React hooks subscribe via useSyncExternalStore for optimal performance
 */
export class SamplePlanState {
  readonly #eventEmitter = new EventTarget();
  readonly #plannedSamples: Map<TeamGlossaryApiId | TempSampleTypeId, PlannedSampleData> = new Map();
  readonly #assignAnimalFormData: Map<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData> = new Map();
  readonly #formValidation: Map<TeamGlossaryApiId, Set<string>> = new Map();
  readonly #duplicateSampleDefinitionNames: Set<string> = new Set();

  #abortController: AbortController = new AbortController();
  #isSubmitting = false;
  #lastFormValid: boolean | null = null;
  #initialDefinitions: Map<SampleDefinitionFormData['api_id'], { hash: string; plannedSampleCount: number }> =
    new Map();
  #affectedSampleCount = 0;
  #cascadeCheckAbortController: AbortController | null = null;

  static readonly EVENTS = {
    PLANNED_SAMPLES_CHANGED: 'plannedSamplesChanged',
    VALIDATION_CHANGED: 'validationChanged',
    FORM_VALIDATION_CHANGED: 'formValidationChanged',
    ANIMAL_ASSIGNMENTS_CHANGED: 'animalAssignmentsChanged',
    DUPLICATE_SAMPLE_DEFINITION_NAMES_DETECTED: 'duplicateSampleDefinitionNamesDetected',
    SUBMISSION_STATE_CHANGED: 'submissionStateChanged',
    CASCADE_UPDATE_DETECTED: 'cascadeUpdateDetected',
  } as const;

  get plannedSamples(): ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, PlannedSampleData> {
    return new Map(this.#plannedSamples);
  }

  get assignAnimalFormData(): ReadonlyMap<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData> {
    return new Map(this.#assignAnimalFormData);
  }

  get formValidation(): ReadonlyMap<TeamGlossaryApiId, ReadonlySet<string>> {
    return new Map(Array.from(this.#formValidation.entries()).map(([k, v]) => [k, new Set(v)]));
  }

  get isSubmitting(): boolean {
    return this.#isSubmitting;
  }

  get affectedSampleCount(): number {
    return this.#affectedSampleCount;
  }

  /**
   * Returns whether this state instance has been destroyed.
   * Use this to check if operations should be aborted.
   */
  isDestroyed(): boolean {
    return this.#abortController.signal.aborted;
  }

  plannedSamplesHasKey(type: TeamGlossaryApiId | TempSampleTypeId): boolean {
    return this.#plannedSamples.has(type);
  }

  getPlannedSample(type: TeamGlossaryApiId | TempSampleTypeId): PlannedSampleData | undefined {
    return this.#plannedSamples.get(type);
  }

  /**
   * Initializes state from existing sample definitions.
   * Clears all existing state and rebuilds from provided definitions.
   * Also initializes cascade detection to track changes that affect planned samples.
   *
   * @param sampleDefinitions - Array of sample definitions to initialize from
   */
  initializeFromSampleDefinitions(sampleDefinitions: Array<SampleDefinitionFormData>): void {
    this.#plannedSamples.clear();
    this.#assignAnimalFormData.clear();

    const plannedSamples = fromSampleDefinitions(sampleDefinitions);
    plannedSamples.forEach((sampleData: PlannedSampleData, type: TeamGlossaryApiId | TempSampleTypeId) => {
      this.#plannedSamples.set(type, sampleData);
    });

    sampleDefinitions.forEach(({ api_id, target }) => {
      this.#assignAnimalFormData.set(api_id, target);
    });

    this.#initializeCascadeDetection(sampleDefinitions);

    this.#emitPlannedSamplesChanged();
    this.#emitAnimalAssignmentsChanged();
  }

  /**
   * Initializes cascade detection by storing hashes of initial sample definitions.
   * Only tracks definitions with planned samples (count > 0) and real API IDs (no temp IDs).
   * Hashes include sample type, detail values/units, and metadata values.
   *
   * @param sampleDefinitions - Sample definitions to baseline against
   */
  async #initializeCascadeDetection(sampleDefinitions: Array<SampleDefinitionFormData>): Promise<void> {
    this.#initialDefinitions.clear();

    const definitionsWithSamples = sampleDefinitions.filter((def) => {
      const plannedSampleCount = def.collected_samples_count?.totals?.planned ?? 0;
      return plannedSampleCount > 0 && isApiId('sdf', def.api_id, false);
    });

    const signal = new AbortController().signal;
    await Promise.all(
      definitionsWithSamples.map(async (def) => {
        const hash = await this.#getCascadingFieldsHash(def, signal);
        const plannedSampleCount = def.collected_samples_count!.totals!.planned!;
        this.#initialDefinitions.set(def.api_id, { hash, plannedSampleCount });
      })
    );

    this.#affectedSampleCount = 0;
  }

  /**
   * Updates or adds sample data for a sample type.
   * Triggers cascade detection and emits events based on source.
   *
   * @param data - Sample data to set
   * @param replaceType - Optional type to replace (for type changes)
   * @param source - 'user' emits PLANNED_SAMPLES_CHANGED, 'hot' skips emit to prevent loops
   */
  setSampleData(
    data: SampleData,
    replaceType?: TempSampleTypeId | TeamGlossaryApiId,
    source: 'hot' | 'user' = 'user'
  ): void {
    if (_notNil(replaceType) && replaceType !== data.type) {
      this.#plannedSamples.delete(replaceType);
    }

    this.#plannedSamples.set(data.type, data);
    if (_isEmpty(data.definitions)) {
      this.#formValidation.set(data.type, new Set(['root.no_definitions']));
      this.#emitFormValidationChanged();
    }
    this.#updateSampleDefinitionNames();

    this.#checkForCascadingUpdates();

    if (source === 'user') {
      this.#emitPlannedSamplesChanged(data.type);
    }
  }

  removeSampleData(type: TeamGlossaryApiId | TempSampleTypeId): void {
    if (this.#plannedSamples.has(type)) {
      this.#plannedSamples.delete(type);
      this.#updateSampleDefinitionNames();
      this.#emitPlannedSamplesChanged(type);
      if (this.#formValidation.delete(type as TeamGlossaryApiId)) {
        this.#emitFormValidationChanged();
      }
    }
  }

  createTempSample(): TempSampleTypeId {
    const tempData = newTempSampleData();
    this.#plannedSamples.set(tempData.type, tempData);
    this.#emitPlannedSamplesChanged(tempData.type);
    return tempData.type;
  }

  /**
   * Replaces all animal target assignments.
   * Triggers cascade detection as animal assignments can affect planned samples.
   *
   * @param assignments - Map of sample definition IDs to their animal target assignments
   */
  setAnimalTargets(assignments: Map<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData>): void {
    this.#assignAnimalFormData.clear();
    assignments.forEach((target, apiId) => {
      this.#assignAnimalFormData.set(apiId, target);
    });
    this.#emitAnimalAssignmentsChanged();
  }

  /**
   * Updates the unit for a sample detail type (e.g., mg → g).
   * Triggers cascade detection as unit changes affect planned samples.
   *
   * @param type - Sample type to update
   * @param detail - Detail type (weight, volume, etc.)
   * @param unit - New unit to apply
   */
  updateSampleDetailUnit(type: TeamGlossaryApiId, detail: SampleDetailType, unit: SampleDetailUnit): void {
    const sampleData = this.#plannedSamples.get(type);
    if (_notNil(sampleData) && isSampleData(sampleData)) {
      const updatedData = { ...sampleData, [detail]: unit };
      this.#plannedSamples.set(type, updatedData);
      this.#checkForCascadingUpdates();
      this.#emitPlannedSamplesChanged(type);
    }
  }

  /**
   * Changes sample type while preserving compatible data.
   * Cleans up validation for removed detail fields and migrates validation state.
   *
   * @param oldType - Current sample type
   * @param newType - New sample type to change to
   * @param sampleGlossary - Glossary containing sample type definitions
   */
  updateSampleType(
    oldType: TeamGlossaryApiId | TempSampleTypeId,
    newType: TeamGlossaryApiId,
    sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>
  ): void {
    const existingData = this.#plannedSamples.get(oldType);
    const glossaryItem = sampleGlossary.get(newType);

    if (_notNil(glossaryItem)) {
      const details = new Set((glossaryItem.options?.details ?? []) as Array<SampleDetailType>);

      const newData: SampleData = Array.from(details).reduce<SampleData>(
        (acc, detail) => ({
          ...acc,
          [detail]: isSampleData(existingData)
            ? (existingData[detail] ?? this.#getDefaultUnit(detail))
            : this.#getDefaultUnit(detail),
        }),
        {
          type: newType,
          definitions: isSampleData(existingData)
            ? this.#filterDefinitionsByDetails(existingData.definitions, details)
            : [newEmptySampleDefinition(`${_isNotBlank(glossaryItem.title) ? glossaryItem.title : 'Sample'} 1`)],
        } as SampleData
      );

      if (isApiId('tgy', oldType, false)) {
        this.#cleanupValidationForSampleType(oldType, details);
      }

      if (oldType !== newType) {
        this.#plannedSamples.delete(oldType);
        if (isApiId('tgy', oldType, false)) {
          const remainingValidation = this.#formValidation.get(oldType);
          if ((remainingValidation?.size ?? 0) > 0) {
            this.#formValidation.set(newType, new Set(remainingValidation));
            this.#formValidation.delete(oldType);
          }
        }
      }
      this.#plannedSamples.set(newType, newData);
      this.#updateSampleDefinitionNames();

      this.#checkForCascadingUpdates();
      this.#emitPlannedSamplesChanged(newType);
    }
  }

  /**
   * Updates validation state for a specific field.
   * Only emits events when validation state actually changes to prevent unnecessary re-renders.
   *
   * @param sampleType - Sample type the field belongs to
   * @param field - Field name (e.g., 'weight.0', 'name.1')
   * @param isValid - Whether the field is valid
   */
  setValidation(sampleType: TeamGlossaryApiId, field: string, isValid: boolean): void {
    const validationData = this.#formValidation.get(sampleType) ?? new Set<string>();

    const hadError = validationData.has(field);
    if (isValid && !hadError) {
      return;
    }
    if (!isValid && hadError) {
      return;
    }

    if (isValid) {
      validationData.delete(field);
    } else {
      validationData.add(field);
    }

    if (validationData.size === 0) {
      this.#formValidation.delete(sampleType);
    } else {
      this.#formValidation.set(sampleType, validationData);
    }

    this.#emitValidationChanged(sampleType, field, isValid);
  }

  isUniqueDefinitionName(name: Nullable<string>): boolean {
    return !this.#duplicateSampleDefinitionNames.has(name?.trim()?.toLowerCase() ?? '');
  }

  setSubmitting(isSubmitting: boolean): void {
    if (this.#isSubmitting !== isSubmitting) {
      this.#isSubmitting = isSubmitting;
      this.#emitSubmissionStateChanged();
    }
  }

  /**
   * Cleans up all internal state and aborts in-flight operations.
   * Should be called when the component unmounts to prevent memory leaks
   * and state updates on unmounted components.
   */
  destroy(): void {
    // Abort the main controller (stops all ongoing operations)
    this.#abortController.abort();

    // Abort cascade detection controller if separate
    if (this.#cascadeCheckAbortController !== null) {
      this.#cascadeCheckAbortController.abort();
      this.#cascadeCheckAbortController = null;
    }

    // Clear all maps and sets to release memory
    this.#plannedSamples.clear();
    this.#assignAnimalFormData.clear();
    this.#formValidation.clear();
    this.#duplicateSampleDefinitionNames.clear();
    this.#initialDefinitions.clear();

    // Reset state flags
    this.#isSubmitting = false;
    this.#lastFormValid = null;
    this.#affectedSampleCount = 0;
  }

  /**
   * Registers an event listener for state changes.
   * Use with AbortController signal for automatic cleanup.
   *
   * @param type - Event type from EVENTS constants
   * @param listener - Callback function to handle the event
   * @param options - Standard EventListener options (signal, once, etc.)
   */
  addEventListener<K extends keyof SamplePlanStateEventMap>(
    type: K,
    listener: (event: SamplePlanStateEventMap[K]) => void,
    options?: AddEventListenerOptions
  ): void {
    this.#eventEmitter.addEventListener(type, listener as EventListener, options);
  }

  /**
   * Computes SHA-512 hash of cascading fields (sample type, details, metadata).
   * Sorts arrays and filters null/empty values for consistent comparison.
   * Checks signal.aborted after hash generation to allow early termination.
   *
   * @param definition - Sample definition to hash
   * @param signal - AbortSignal to check for cancellation
   * @returns SHA-512 hash of cascading fields
   */
  async #getCascadingFieldsHash(definition: SampleDefinitionFormData, signal: AbortSignal): Promise<string> {
    if (signal.aborted) {
      return '';
    }

    const cascadingFields = {
      sample_type: definition.sample_type,
      details: definition.details
        .filter((d) => _notNil(d.value) && _isNotBlank(String(d.value)))
        .map((d) => ({
          type: d.type,
          value: d.value,
          unit: d.unit,
        }))
        .sort((a, b) => a.type.localeCompare(b.type)),
      metadata: definition.metadata
        .filter((m) => _notNil(m.value) && _isNotBlank(String(m.value)))
        .map((m) => ({
          type: m.type,
          value: m.value,
        }))
        .sort((a, b) => a.type.localeCompare(b.type)),
    };

    const serialized = JSON.stringify(cascadingFields);
    const hash = await generateIntegrity(serialized);

    if (signal.aborted) {
      return '';
    }

    return hash;
  }

  #updateSampleDefinitionNames(): void {
    const counts = Array.from(this.#plannedSamples.values()).reduce((acc, sampleData) => {
      if (isSampleData(sampleData)) {
        sampleData.definitions.forEach((definition) => {
          const value = definition.name?.trim()?.toLowerCase() ?? '';
          if (_isNotBlank(value)) {
            acc.set(value, (acc.get(value) ?? 0) + 1);
          }
        });
      }
      return acc;
    }, new Map<string, number>());

    const update: Set<string> = Array.from(counts.entries()).reduce(
      (acc, [name, count]) => (count > 1 ? acc.add(name) : acc),
      new Set<string>()
    );

    const isUpdate =
      setsDifference(this.#duplicateSampleDefinitionNames, update).size > 0 ||
      setsDifference(update, this.#duplicateSampleDefinitionNames).size > 0;
    this.#duplicateSampleDefinitionNames.clear();
    update.forEach((name) => this.#duplicateSampleDefinitionNames.add(name));
    if (isUpdate) {
      this.#emitDuplicateSampleDefinitionNamesDetected();
    }
  }

  #emitPlannedSamplesChanged(changedType?: TeamGlossaryApiId | TempSampleTypeId): void {
    const event = new CustomEvent(SamplePlanState.EVENTS.PLANNED_SAMPLES_CHANGED, {
      detail: { plannedSamples: this.plannedSamples, changedType },
    });
    this.#eventEmitter.dispatchEvent(event);
  }

  #emitDuplicateSampleDefinitionNamesDetected(): void {
    const event = new CustomEvent(SamplePlanState.EVENTS.DUPLICATE_SAMPLE_DEFINITION_NAMES_DETECTED, {
      detail: { duplicates: new Set(this.#duplicateSampleDefinitionNames) },
    });
    this.#eventEmitter.dispatchEvent(event);
  }

  #emitValidationChanged(sampleType: TeamGlossaryApiId, field: string, isValid: boolean): void {
    this.#eventEmitter.dispatchEvent(
      new CustomEvent(SamplePlanState.EVENTS.VALIDATION_CHANGED, {
        detail: { sampleType, field, isValid, allValidation: this.formValidation },
      })
    );
    this.#emitFormValidationChanged();
  }

  #getDefaultUnit(detail: SampleDetailType): SampleDetailUnit {
    const detailUnits: Record<SampleDetailType, Array<{ value: SampleDetailUnit; label: SampleDetailUnit }>> = {
      weight: [
        { value: 'µg', label: 'µg' },
        { value: 'mg', label: 'mg' },
        { value: 'g', label: 'g' },
      ],
      volume: [
        { value: 'µl', label: 'µl' },
        { value: 'ml', label: 'ml' },
        { value: 'cm³', label: 'cm³' },
        { value: 'mm³', label: 'mm³' },
      ],
      width: [{ value: 'mm', label: 'mm' }],
      length: [{ value: 'mm', label: 'mm' }],
      depth: [{ value: 'mm', label: 'mm' }],
    };
    return detailUnits[detail][0].value;
  }

  #filterDefinitionsByDetails(
    definitions: Array<SampleDefinitionRowData>,
    allowedDetails: Set<SampleDetailType>
  ): Array<SampleDefinitionRowData> {
    return definitions.map((definition) => {
      const filteredDefinition = { ...definition };
      Object.keys(SampleDefinitionDetailTypes).forEach((key) => {
        if (!allowedDetails.has(key as SampleDetailType)) {
          delete filteredDefinition[key as SampleDetailType];
        }
      });
      return filteredDefinition;
    });
  }

  #emitAnimalAssignmentsChanged(): void {
    const event = new CustomEvent(SamplePlanState.EVENTS.ANIMAL_ASSIGNMENTS_CHANGED, {
      detail: { assignments: this.assignAnimalFormData },
    });
    this.#eventEmitter.dispatchEvent(event);
    this.#checkForCascadingUpdates();
  }

  #emitFormValidationChanged(): void {
    const valid = Array.from(this.#formValidation.values()).every((validationSet) => validationSet.size === 0);

    if (this.#lastFormValid !== valid) {
      this.#lastFormValid = valid;
      this.#eventEmitter.dispatchEvent(
        new CustomEvent(SamplePlanState.EVENTS.FORM_VALIDATION_CHANGED, {
          detail: { valid },
        })
      );
    }
  }

  #emitSubmissionStateChanged(): void {
    const event = new CustomEvent(SamplePlanState.EVENTS.SUBMISSION_STATE_CHANGED, {
      detail: { isSubmitting: this.#isSubmitting },
    });
    this.#eventEmitter.dispatchEvent(event);
  }

  #cleanupValidationForSampleType(sampleType: TeamGlossaryApiId, validDetails: Set<SampleDetailType>): void {
    const currentValidation = this.#formValidation.get(sampleType);
    if (_isNil(currentValidation)) {
      return;
    }

    let validationChanged = false;

    const fieldsToRemove = Array.from(currentValidation).filter((fieldName) => {
      const detail = fieldName.split('.')?.[1];
      if (_notNil(detail)) {
        return (
          Object.prototype.hasOwnProperty.call(SampleDefinitionDetailTypes, detail as SampleDetailType) &&
          !validDetails.has(detail as SampleDetailType)
        );
      }
      return false;
    });

    fieldsToRemove.forEach((fieldName) => {
      currentValidation.delete(fieldName);
      validationChanged = true;
    });

    if (validationChanged) {
      if (currentValidation.size === 0) {
        this.#formValidation.delete(sampleType);
      }
      this.#emitFormValidationChanged();
    }
  }

  #isCascadeDetectionDisabled(): boolean {
    return this.#initialDefinitions.size === 0;
  }

  /**
   * Reconstructs SampleDefinitionFormData from current UI state.
   * Converts spreadsheet row data back to API format with proper type normalization.
   *
   * @returns Map of definition IDs to reconstructed definitions
   */
  #getCurrentDefinitionsMap(): Map<SampleDefinitionFormData['api_id'], SampleDefinitionFormData> {
    const currentDefinitions = new Map<SampleDefinitionFormData['api_id'], SampleDefinitionFormData>();

    this.#plannedSamples.forEach((sampleData) => {
      if (isSampleData(sampleData)) {
        sampleData.definitions.forEach((rowData) => {
          const details: Array<SampleDefinitionDetailV1> = Object.keys(SampleDefinitionDetailTypes).reduce<
            Array<SampleDefinitionDetailV1>
          >((acc, key) => {
            const detailType = key as SampleDetailType;
            if (_notNil(sampleData[detailType])) {
              const detailValue = rowData[detailType];
              if (_notNil(detailValue) && _isNotBlank(String(detailValue))) {
                acc.push({
                  type: detailType,
                  value: `${detailValue}` as NumericString,
                  unit: sampleData[detailType] as SampleDetailUnit,
                });
              }
            }
            return acc;
          }, []);

          const metadata: Array<SampleDefinitionMetadataV1> = Object.keys(rowData).reduce<
            Array<SampleDefinitionMetadataV1>
          >((acc, key) => {
            if (isApiId('mgl', key)) {
              const value = rowData[key as MetadataGlossaryApiId];
              const stringValue = String(value);

              if (_notNil(value) && _isNotBlank(stringValue) && !_isStringifiedNullish(stringValue)) {
                acc.push({
                  type: key as MetadataGlossaryApiId,
                  value: typeof value === 'object' && value !== null ? JSON.stringify(value) : `${value}`,
                });
              }
            }
            return acc;
          }, []);

          const target = this.#assignAnimalFormData.get(rowData.api_id) ?? { type: 'no_target' };

          currentDefinitions.set(rowData.api_id, {
            api_id: rowData.api_id,
            sample_type: sampleData.type,
            name: rowData.name,
            calculate_collection_time: rowData.calculate_collection_time,
            timepoints: rowData.timepoints ?? [],
            collected_samples_count: rowData.collected_samples_count,
            details,
            metadata,
            target,
          });
        });
      }
    });

    return currentDefinitions;
  }

  async #getChangedDefinitions(signal: AbortSignal): Promise<Set<SampleDefinitionFormData['api_id']>> {
    const changedIds = new Set<SampleDefinitionFormData['api_id']>();
    const currentDefinitionsMap = this.#getCurrentDefinitionsMap();

    const currentHashes = new Map<SampleDefinitionFormData['api_id'], string>();
    await Promise.all(
      Array.from(currentDefinitionsMap.entries()).map(async ([apiId, def]) => {
        if (signal.aborted) {
          return;
        }
        const hash = await this.#getCascadingFieldsHash(def, signal);
        if (signal.aborted) {
          return;
        }
        currentHashes.set(apiId, hash);
      })
    );

    if (signal.aborted) {
      return changedIds;
    }

    this.#initialDefinitions.forEach((initialData, apiId) => {
      const currentHash = currentHashes.get(apiId);

      if (_isNil(currentHash)) {
        return;
      }

      if (initialData.hash !== currentHash) {
        changedIds.add(apiId);
      }
    });

    return changedIds;
  }

  #calculateAffectedSampleCount(changedDefinitionIds: Set<SampleDefinitionFormData['api_id']>): number {
    let count = 0;
    changedDefinitionIds.forEach((apiId) => {
      const initialData = this.#initialDefinitions.get(apiId);
      count += initialData?.plannedSampleCount ?? 0;
    });
    return count;
  }

  /**
   * Triggers async cascade detection without blocking.
   * Aborts any in-flight check before starting a new one to prevent race conditions.
   * Result emitted via CASCADE_UPDATE_DETECTED event when complete.
   */
  #checkForCascadingUpdates(): void {
    if (this.#cascadeCheckAbortController !== null) {
      this.#cascadeCheckAbortController.abort();
    }

    this.#cascadeCheckAbortController = new AbortController();
    const signal = this.#cascadeCheckAbortController.signal;

    this.#checkForCascadingUpdatesAsync(signal).catch((cause) => {
      if (cause instanceof Error && cause.name === 'AbortError') {
        return;
      }
      ExceptionHandler.captureException(
        new InVivoError('Failed to detect cascading sample plan updates', {
          slug: 'sample-plan-cascade-detection',
          cause,
        })
      );
    });
  }

  /**
   * Compares current definitions against initial state to detect cascading changes.
   * Updates affectedSampleCount and emits event if count changes.
   * Checks signal.aborted before updating state to handle cancellation.
   */
  async #checkForCascadingUpdatesAsync(signal: AbortSignal): Promise<void> {
    if (this.#isCascadeDetectionDisabled()) {
      if (signal.aborted) {
        return;
      }
      if (this.#affectedSampleCount !== 0) {
        this.#affectedSampleCount = 0;
        this.#emitCascadeUpdateDetected();
      }
      return;
    }

    const changedDefinitionIds = await this.#getChangedDefinitions(signal);

    if (signal.aborted) {
      return;
    }

    const newCount = this.#calculateAffectedSampleCount(changedDefinitionIds);

    if (signal.aborted) {
      return;
    }

    if (this.#affectedSampleCount !== newCount) {
      this.#affectedSampleCount = newCount;
      this.#emitCascadeUpdateDetected();
    }
  }

  #emitCascadeUpdateDetected(): void {
    const event = new CustomEvent(SamplePlanState.EVENTS.CASCADE_UPDATE_DETECTED, {
      detail: { affectedSampleCount: this.#affectedSampleCount },
    });
    this.#eventEmitter.dispatchEvent(event);
  }
}

type SampleGlossaryEntry = {
  title?: string | null;
  options?: { details: Array<string> };
  archived_at?: string | null;
};

export type AddSamplePlanContext = {
  samplePlanState: SamplePlanState;
  metadataGlossary: Map<MetadataGlossaryApiId, MetadataGlossaryV1>;
  sampleGlossary: Map<TeamGlossaryApiId, SampleGlossaryEntry>;
  sampleOptions: Map<TeamGlossaryApiId, { label: string; value: TeamGlossaryApiId }>;
  groups: Map<GroupTarget['api_id'], GroupTarget>;
  registered_animals: Map<RegisteredAnimalTarget['api_id'], RegisteredAnimalTarget>;
  planned_animals: Map<PlannedAnimalTarget['api_id'], PlannedAnimalTarget>;
  stepInValid: boolean;
  enable_collection_time_calculation: boolean;
  step: Step;
  nextStep: () => void;
  prevStep?: () => void;
};
const valueToString = (value: string | number | boolean | Array<string> | object): string => {
  if (_notNil(value)) {
    if (Array.isArray(value)) {
      return value
        .reduce<Array<string>>((acc, entry) => {
          const entryString = valueToString(entry);
          if (entryString.length > 0) {
            acc.push(entryString);
          }
          return acc;
        }, [])
        .join(',');
    }
    switch (typeof value) {
      case 'string':
        return value;
      case 'number':
        return Number.isFinite(value) ? `${value}` : '';
      case 'boolean':
        return `${value}`;
      case 'object': // Ignore collect sample counts object
        return '';
    }
  }
  return '';
};

type PlannedSamplesChangedEvent = CustomEvent<{
  plannedSamples: ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, PlannedSampleData>;
  changedType?: TeamGlossaryApiId | TempSampleTypeId;
}>;

type ValidationChangedEvent = CustomEvent<{
  sampleType: TeamGlossaryApiId;
  field: string;
  isValid: boolean;
  allValidation: ReadonlyMap<TeamGlossaryApiId, ReadonlySet<string>>;
}>;

type AnimalAssignmentsChangedEvent = CustomEvent<{
  assignments: ReadonlyMap<SampleDefinitionFormData['api_id'], SampleDefinitionTargetFormData>;
}>;

type DuplicateSampleDefinitionNamesDetectedEvent = CustomEvent<{ duplicates: ReadonlySet<string> }>;

type FormValidationChangedEvent = CustomEvent<{ valid: boolean }>;

type SubmissionStateChangedEvent = CustomEvent<{ isSubmitting: boolean }>;

type CascadeUpdateDetectedEvent = CustomEvent<{
  affectedSampleCount: number;
}>;

interface SamplePlanStateEventMap {
  [SamplePlanState.EVENTS.PLANNED_SAMPLES_CHANGED]: PlannedSamplesChangedEvent;
  [SamplePlanState.EVENTS.VALIDATION_CHANGED]: ValidationChangedEvent;
  [SamplePlanState.EVENTS.FORM_VALIDATION_CHANGED]: FormValidationChangedEvent;
  [SamplePlanState.EVENTS.ANIMAL_ASSIGNMENTS_CHANGED]: AnimalAssignmentsChangedEvent;
  [SamplePlanState.EVENTS.DUPLICATE_SAMPLE_DEFINITION_NAMES_DETECTED]: DuplicateSampleDefinitionNamesDetectedEvent;
  [SamplePlanState.EVENTS.SUBMISSION_STATE_CHANGED]: SubmissionStateChangedEvent;
  [SamplePlanState.EVENTS.CASCADE_UPDATE_DETECTED]: CascadeUpdateDetectedEvent;
}

export const usePlannedSamples = (): ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, PlannedSampleData> => {
  const { samplePlanState } = useAddSamplePlanContext();
  const [plannedSamples, setPlannedSamples] = useState(() => samplePlanState.plannedSamples);

  useEffect(() => {
    const abortController = new AbortController();

    const handler = (event: PlannedSamplesChangedEvent) => {
      setPlannedSamples(event.detail.plannedSamples);
    };

    samplePlanState.addEventListener(SamplePlanState.EVENTS.PLANNED_SAMPLES_CHANGED, handler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [samplePlanState]);

  return plannedSamples;
};

export const usePlannedSample = (type: TeamGlossaryApiId | TempSampleTypeId): PlannedSampleData | null => {
  const { samplePlanState } = useAddSamplePlanContext();
  const [sampleData, setSampleData] = useState(() => samplePlanState.getPlannedSample(type) ?? null);

  useEffect(() => {
    const abortController = new AbortController();

    const handler = (event: PlannedSamplesChangedEvent) => {
      if (event?.detail?.changedType === type) {
        setSampleData(event.detail.plannedSamples.get(type) ?? null);
      }
    };

    samplePlanState.addEventListener(SamplePlanState.EVENTS.PLANNED_SAMPLES_CHANGED, handler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [samplePlanState, type]);

  return sampleData;
};

export const usePlannedSampleTypes = (): ReadonlyArray<TeamGlossaryApiId | TempSampleTypeId> => {
  const { samplePlanState } = useAddSamplePlanContext();
  const [sampleTypes, setSampleTypes] = useState(() => Array.from(samplePlanState.plannedSamples.keys()));

  useEffect(() => {
    const abortController = new AbortController();

    const handler = (event: PlannedSamplesChangedEvent) => {
      const newTypes = Array.from(event.detail.plannedSamples.keys());
      setSampleTypes((prevTypes) => {
        if (prevTypes.length !== newTypes.length || !prevTypes.every((type, index) => type === newTypes[index])) {
          return newTypes;
        }
        return prevTypes;
      });
    };

    samplePlanState.addEventListener(SamplePlanState.EVENTS.PLANNED_SAMPLES_CHANGED, handler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [samplePlanState]);

  return sampleTypes;
};

/**
 * React hook that subscribes to the sample plan submission state.
 * Returns true when a form submission is in progress, false otherwise.
 *
 * This hook uses an event-driven approach to avoid unnecessary re-renders.
 * Unlike putting isSubmitting in context (which would re-render ALL context consumers),
 * this hook only triggers re-renders in components that specifically call it.
 *
 * @returns boolean indicating whether the form is currently submitting
 *
 */
export const useIsSubmitting = (): boolean => {
  const { samplePlanState } = useAddSamplePlanContext();
  const [isSubmitting, setIsSubmitting] = useState(() => samplePlanState.isSubmitting);

  useEffect(() => {
    const abortController = new AbortController();

    const handler = (event: SubmissionStateChangedEvent) => {
      setIsSubmitting(event.detail.isSubmitting);
    };

    samplePlanState.addEventListener(SamplePlanState.EVENTS.SUBMISSION_STATE_CHANGED, handler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [samplePlanState]);

  return isSubmitting;
};

export const useCascadeUpdateWarning = (): number => {
  const { samplePlanState } = useAddSamplePlanContext();

  const subscribe = useCallback(
    (callback: () => void) => {
      const abortController = new AbortController();
      samplePlanState.addEventListener(SamplePlanState.EVENTS.CASCADE_UPDATE_DETECTED, callback, {
        signal: abortController.signal,
      });
      return () => abortController.abort();
    },
    [samplePlanState]
  );

  const getSnapshot = useCallback(() => samplePlanState.affectedSampleCount, [samplePlanState]);

  return useSyncExternalStore(subscribe, getSnapshot);
};

const newTempSampleData = (): TempSampleData => ({ type: generateTempApiId('tgl') });
export const isSampleData = (value: Nullable<PlannedSampleData>): value is SampleData =>
  _notNil(value) && value.type.startsWith('tgy_');
const sampleDefinitionDetailTypePattern = /^(weight|volume|width|length|depth)$/;
const isSampleDefinitionDetailType = (value: string | undefined): value is SampleDetailType =>
  sampleDefinitionDetailTypePattern.test(value ?? '');

const fromSampleDefinitions = (
  sampleDefinitions: Array<SampleDefinitionFormData>
): ReadonlyMap<TeamGlossaryApiId | TempSampleTypeId, PlannedSampleData> => {
  if (sampleDefinitions.length === 0) {
    const tmpSampleData = newTempSampleData();
    return new Map().set(tmpSampleData.type, tmpSampleData);
  }
  const { groupedSampleDefinitions, detailUnitsByType } = sampleDefinitions.reduce<{
    groupedSampleDefinitions: Map<TeamGlossaryApiId, Array<SampleDefinitionRowData>>;
    detailUnitsByType: Map<TeamGlossaryApiId, Partial<Record<SampleDetailType, SampleDetailUnit>>>;
  }>(
    (acc, sampleDefinition) => {
      const details = sampleDefinition.details.reduce<{
        units: Partial<Record<SampleDetailType, SampleDetailUnit>>;
        values: Partial<Record<SampleDetailType, string>>;
      }>(
        (detailAcc, detail) => {
          detailAcc.units[detail.type] = detail.unit;
          if (_isNotBlank(detail.value)) {
            detailAcc.values[detail.type] = detail.value;
          }
          return detailAcc;
        },
        { units: {}, values: {} }
      );
      if (!acc.detailUnitsByType.has(sampleDefinition.sample_type)) {
        acc.detailUnitsByType.set(sampleDefinition.sample_type, {});
      }
      const existingUnits = acc.detailUnitsByType.get(sampleDefinition.sample_type)!;
      Object.assign(existingUnits, details.units);

      const sampleDefinitionData: SampleDefinitionRowData = {
        api_id: sampleDefinition.api_id,
        name: sampleDefinition.name,
        timepoints: sampleDefinition.timepoints,
        calculate_collection_time: sampleDefinition.calculate_collection_time,
        collected_samples_count: sampleDefinition.collected_samples_count,
        ...details.values,
        ...sampleDefinition.metadata.reduce<Record<MetadataGlossaryApiId, string>>((acc, meta) => {
          if (_isNotBlank(meta.type) && _isNotBlank(meta.value)) {
            acc[meta.type as MetadataGlossaryApiId] = meta.value;
          }
          return acc;
        }, {}),
      };

      acc.groupedSampleDefinitions.set(sampleDefinition.sample_type, [
        ...(acc.groupedSampleDefinitions.get(sampleDefinition.sample_type) ?? []),
        sampleDefinitionData,
      ]);
      return acc;
    },
    { groupedSampleDefinitions: new Map(), detailUnitsByType: new Map() }
  );
  return new Map(
    Array.from(groupedSampleDefinitions.entries()).map<[SampleData['type'], SampleData]>(
      ([sampleType, definitions]) => [
        sampleType,
        { type: sampleType, definitions, ...(detailUnitsByType.get(sampleType) ?? {}) },
      ]
    )
  );
};

export const newEmptySampleDefinition = (name = ''): SampleDefinitionRowData => {
  return {
    api_id: generateTempApiId('sdf'),
    name,
    timepoints: [],
    calculate_collection_time: false,
  };
};
const context = createContext<AddSamplePlanContext>({
  samplePlanState: new SamplePlanState(),
  sampleGlossary: new Map(),
  sampleOptions: new Map(),
  metadataGlossary: new Map(),
  groups: new Map(),
  registered_animals: new Map(),
  planned_animals: new Map(),
  stepInValid: true,
  enable_collection_time_calculation: false,
  step: 'sample_type_define',
  nextStep: _noop,
  prevStep: _noop,
});
type AddSamplePlanProviderProps = {
  onSubmit?: OnSubmitFunction;
  groups: Array<GroupTarget>;
  registered_animals: Array<RegisteredAnimalTarget>;
  planned_animals: Array<PlannedAnimalTarget>;
  sample_definitions: Array<SampleDefinitionFormData>;
};
export const enableCollectionTimeCalculationSelector = createSelector(
  [featuresSelector],
  (features) => features?.sampling_sheets === true
);
const reasonForChangeEnabledSelector = createSelector(
  [featuresSelector],
  (features) => features?.reason_for_change === true
);
export const useAddSamplePlanContext = () => useContext(context);
export const AddSamplePlanProvider: FC<PropsWithChildren<AddSamplePlanProviderProps>> = ({
  children,
  groups,
  registered_animals,
  planned_animals,
  sample_definitions,
  onSubmit,
}) => {
  const { response: sampleMetadataGlossaryResponse, loading: sampleMetadataGlossaryLoading } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-meta-load' } },
  });
  const { response: sampleGlossaryResponse, loading: sampleGlossaryLoading } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      order: 'asc',
      sort: 'title',
    },
    options: { onError: { slug: 'samples-glossary-load' } },
  });
  const enable_collection_time_calculation = useSelector(enableCollectionTimeCalculationSelector);
  const reasonForChangeEnabled = useSelector(reasonForChangeEnabledSelector);
  const { openModal } = useModalAction();
  const samplePlanState = useRef(new SamplePlanState());
  const [step, updateStep] = useState<Step>('sample_type_define');
  const [formValid, setFormValid] = useState(() => samplePlanState.current.formValidation.size === 0);

  useEffect(() => {
    samplePlanState.current.initializeFromSampleDefinitions(sample_definitions);
  }, [sample_definitions]);

  useEffect(() => {
    const abortController = new AbortController();

    const formValidationHandler = (event: CustomEvent<{ valid: boolean }>) => {
      setFormValid(event.detail.valid);
    };

    samplePlanState.current.addEventListener(SamplePlanState.EVENTS.FORM_VALIDATION_CHANGED, formValidationHandler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    return () => {
      samplePlanState.current.destroy();
    };
  }, []);

  const groupMap = useMemo(() => new Map(groups.map((group) => [group.api_id, group])), [groups]);
  const registeredAnimalMap = useMemo(
    () =>
      new Map<RegisteredAnimalTarget['api_id'], RegisteredAnimalTarget>(
        registered_animals.map((animal) => [animal.api_id, animal])
      ),
    [registered_animals]
  );
  const plannedAnimalMap = useMemo(
    () =>
      new Map<PlannedAnimalTarget['api_id'], PlannedAnimalTarget>(
        planned_animals.map((plannedAnimal) => [plannedAnimal.api_id, plannedAnimal])
      ),
    [planned_animals]
  );

  const metadataGlossary = useMemo(() => {
    if (sampleMetadataGlossaryResponse?.type === 'success') {
      return new Map(
        sampleMetadataGlossaryResponse.body.map((glossary) => [glossary.api_id as MetadataGlossaryApiId, glossary])
      );
    }
    return new Map<MetadataGlossaryApiId, MetadataGlossaryV1>();
  }, [sampleMetadataGlossaryResponse]);
  const sampleContext = useMemo(() => {
    if (sampleGlossaryResponse?.type === 'success') {
      return (
        sampleGlossaryResponse.body as {
          data: Array<TeamGlossary>;
        }
      ).data.reduce<Pick<AddSamplePlanContext, 'sampleOptions' | 'sampleGlossary'>>(
        (acc, glossary) => {
          acc.sampleGlossary.set(glossary.api_id, glossary);
          acc.sampleOptions.set(glossary.api_id, {
            value: glossary.api_id,
            label: glossary?.title ?? '',
          });
          return acc;
        },
        { sampleGlossary: new Map(), sampleOptions: new Map() }
      );
    }
    return { sampleGlossary: new Map(), sampleOptions: new Map() };
  }, [sampleGlossaryResponse]);

  /**
   * Advances the sample plan workflow to the next step.
   *
   * Flow:
   * 1. sample_type_define → assign_animals (when form is valid)
   * 2. assign_animals → submission (transforms plannedSamples to API format)
   *
   * During submission (step 2), this function:
   * - Extracts sample definitions from planned samples
   * - Filters metadata and detail fields based on glossary configuration
   * - Preserves empty details with configured units
   * - Computes deleted sample IDs for removal
   * - Calls onSubmit with transformed data
   */
  const nextStep = useCallback(() => {
    switch (step) {
      case 'sample_type_define': {
        if (samplePlanState.current.formValidation.size === 0) {
          updateStep('assign_animals');
        }
        break;
      }
      case 'assign_animals': {
        if (samplePlanState.current.formValidation.size === 0) {
          if (typeof onSubmit === 'function') {
            const currentPlannedSamples = samplePlanState.current.plannedSamples;
            const currentAssignments = samplePlanState.current.assignAnimalFormData;

            const { updatedDefinitions, updatedIds } = Array.from(currentPlannedSamples.values()).reduce<{
              updatedDefinitions: Array<SampleDefinitionFormData>;
              updatedIds: Set<SampleDefinitionFormData['api_id']>;
            }>(
              (acc, plannedSample) => {
                if (isSampleData(plannedSample)) {
                  const validDetails = new Set(
                    (sampleContext.sampleGlossary.get(plannedSample.type)?.options?.details ??
                      []) as Array<SampleDetailType>
                  );
                  plannedSample.definitions.forEach((sampleDefinition) => {
                    const { details, metadata } = Object.entries(sampleDefinition).reduce<
                      RequiredNonNil<Pick<SampleDefinitionFormData, 'details' | 'metadata'>>
                    >(
                      (acc, [key, value]) => {
                        const stringValue = valueToString(value);
                        if (_isNotBlank(stringValue)) {
                          if (isApiId('mgl', key)) {
                            // Defense-in-depth: a metadata cell may carry the coerced-nil sentinel
                            // strings "undefined"/"null" (legacy data, or any editor that wrote them
                            // before the MultiSelectEditorV16 nil-safety fix). The API rejects these,
                            // so treat them as empty and omit the field rather than sending garbage.
                            // Mirrors the guard already used in #getCurrentDefinitionsMap().
                            if (!_isStringifiedNullish(stringValue)) {
                              acc.metadata.push({ type: key, value: stringValue });
                            }
                          } else if (isSampleDefinitionDetailType(key) && validDetails.has(key)) {
                            acc.details.push({
                              type: key,
                              value: stringValue as NumericString,
                              unit: plannedSample[key] as SampleDetailUnit,
                            });
                          }
                        }
                        return acc;
                      },
                      { details: [], metadata: [] }
                    );

                    const existingDetailTypes = new Set(details.map((d) => d.type));
                    validDetails.forEach((detailType) => {
                      if (!existingDetailTypes.has(detailType) && _notNil(plannedSample[detailType])) {
                        details.push({
                          type: detailType,
                          value: null,
                          unit: plannedSample[detailType] as SampleDetailUnit,
                        });
                      }
                    });

                    const sampleDefinitionData = {
                      api_id: sampleDefinition.api_id,
                      sample_type: plannedSample.type,
                      name: sampleDefinition.name,
                      calculate_collection_time: sampleDefinition.calculate_collection_time,
                      timepoints: sampleDefinition.timepoints ?? [],
                      target: currentAssignments.get(sampleDefinition.api_id) ?? { type: 'no_target' },
                      details,
                      metadata,
                    };

                    acc.updatedDefinitions.push(sampleDefinitionData);
                    acc.updatedIds.add(sampleDefinition.api_id);
                  });
                }
                return acc;
              },
              { updatedDefinitions: [], updatedIds: new Set<SampleDefinitionFormData['api_id']>() }
            );

            const deletedIds = setsDifference(new Set(sample_definitions.map((sd) => sd.api_id)), updatedIds);

            const proceedWithSubmission = async (reasonForChange?: string): Promise<void> => {
              if (!samplePlanState.current.isDestroyed()) {
                samplePlanState.current.setSubmitting(true);
              }
              try {
                await onSubmit(updatedDefinitions, deletedIds, reasonForChange);
              } catch {
                errorToast('Could not update sample plan');
              } finally {
                if (!samplePlanState.current.isDestroyed()) {
                  samplePlanState.current.setSubmitting(false);
                }
              }
            };

            const affectedCount = samplePlanState.current.affectedSampleCount;
            const isEditingPersistedPlan = sample_definitions.some((sd) => isApiId('sdf', sd.api_id, false));

            if (affectedCount > 0 || (reasonForChangeEnabled && isEditingPersistedPlan)) {
              openModal('CONFIRM_CASCADE_UPDATE_SAMPLE_PLAN', {
                affectedSampleCount: affectedCount,
                onClick: (reasonForChange?: string) => proceedWithSubmission(reasonForChange),
              });
            } else {
              if (!samplePlanState.current.isDestroyed()) {
                proceedWithSubmission();
              }
            }
          }
        }
        break;
      }
    }
  }, [step, sample_definitions, onSubmit, sampleContext, reasonForChangeEnabled]);

  const prevStep = useCallback(() => {
    if (step === 'assign_animals') {
      updateStep('sample_type_define');
    }
  }, [step]);

  const contextValue = useMemo<AddSamplePlanContext>(
    () => ({
      ...sampleContext,
      samplePlanState: samplePlanState.current,
      metadataGlossary,
      groups: groupMap,
      registered_animals: registeredAnimalMap,
      planned_animals: plannedAnimalMap,
      stepInValid: !formValid,
      enable_collection_time_calculation,
      step,
      nextStep,
      prevStep: step === 'assign_animals' ? prevStep : undefined,
    }),
    [
      sampleContext,
      metadataGlossary,
      groupMap,
      registeredAnimalMap,
      plannedAnimalMap,
      step,
      nextStep,
      prevStep,
      formValid,
      enable_collection_time_calculation,
    ]
  );

  if (sampleMetadataGlossaryLoading || sampleGlossaryLoading) {
    return <Loading />;
  }
  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
